import { create } from 'zustand';
import type { Platform } from '@/lib/types';
import type { PlatformAdapter } from '@/adapters/PlatformAdapter';
import { FrontAdapter } from '@/adapters/FrontAdapter';
import { useConversationStore } from './conversationStore';

interface PlatformState {
  platform: Platform | null;
  adapter: PlatformAdapter | null;
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;

  initializePlatform: () => Promise<void>;
  setPlatform: (platform: Platform, adapter: PlatformAdapter) => void;
}

export const usePlatformStore = create<PlatformState>((set, get) => ({
  platform: null,
  adapter: null,
  isInitialized: false,
  isInitializing: false,
  error: null,

  initializePlatform: async () => {
    console.log('[PlatformStore] initializePlatform called');

    // Prevent duplicate initialization
    const state = get();
    if (state.isInitializing) {
      console.warn('[PlatformStore] Already initializing, skipping duplicate call');
      return;
    }
    if (state.isInitialized) {
      console.warn('[PlatformStore] Already initialized, skipping duplicate call');
      return;
    }

    // Set initializing flag
    set({ isInitializing: true });

    try {
      // Detect platform from URL or environment
      // For now, default to Front
      const platform: Platform = 'front';

      let adapter: PlatformAdapter;

      if (platform === 'front') {
        console.log('[PlatformStore] Creating FrontAdapter');
        adapter = new FrontAdapter();
      } else if (platform === 'missive') {
        // adapter = new MissiveAdapter();
        throw new Error('Missive adapter not yet implemented');
      } else if (platform === 'native') {
        throw new Error('Native platform not yet implemented');
      } else {
        throw new Error(`Unknown platform: ${platform}`);
      }

      // This will throw if Front SDK is unavailable and mock data is disabled
      console.log('[PlatformStore] Calling adapter.initialize()');
      await adapter.initialize();
      console.log('[PlatformStore] Adapter initialized');

      // Set up conversation change listener
      adapter.onConversationChange((conversation) => {
        console.log('[PlatformStore] Conversation changed callback fired:', conversation);
        // Reset analysis state BEFORE setting new conversation
        useConversationStore.getState().reset();
        useConversationStore.getState().setConversation(conversation);
      });

      // Get initial conversation (may be null for Front SDK - waits for contextUpdates)
      const initialConversation = await adapter.getCurrentConversation();
      if (initialConversation) {
        console.log('[PlatformStore] Setting initial conversation:', initialConversation);
        useConversationStore.getState().setConversation(initialConversation);
      } else {
        console.log('[PlatformStore] No initial conversation - waiting for platform updates');
      }

      // Mark as initialized - adapter is ready to receive updates
      set({
        platform,
        adapter,
        isInitialized: true,
        isInitializing: false,
        error: null,
      });

      console.log('[PlatformStore] Platform initialized successfully:', platform);
    } catch (error) {
      console.error('[PlatformStore] Platform initialization error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize platform';

      // Always set isInitialized to true so we don't block forever
      // Error will be shown in error state
      set({
        error: errorMessage,
        isInitialized: true, // Important: don't block forever
        isInitializing: false,
        platform: null,
        adapter: null,
      });

      // Re-throw so caller knows initialization failed
      throw error;
    }
  },

  setPlatform: (platform, adapter) => {
    set({ platform, adapter, isInitialized: true });
  },
}));
